// features/workstations/tabs/catalyst.js
// ═══════════════════════════════════════════════════════════
// CATALYST TAB — Catalyst Research methodology surface
// Long-Term 10x ONLY (gated by isTabEnabled in config-schema.js).
//
// Five sections:
//   1. Open Predictions — currently locked predictions + countdowns
//      to D+30 (empty until Phase 8 of build)
//   2. Hypotheses       — H-001v2 + 6 children with status badges,
//      links to markdown cards in repo
//   3. Track Record     — scored count / excluded count / sustain
//      rate / calibration plot (empty until Phase 9)
//   4. New Prediction   — form scaffold to lock a stimulus card
//      (visible but not wired to write yet — Phase 2 of UI)
//   5. Methodology      — current version, change log, library
//      version, retired-hypothesis history
//
// Reads from:
//   /api/workstations/catalyst/list-hypotheses
//   /api/workstations/catalyst/list-change-log
//
// Spec: docs/PENNY_CATALYST_SUSTAIN_BUILD.md (build doc v2.0)
//       docs/CATALYST_RESEARCH_FRAMEWORK.md (methodology framework)
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { SectionedTab } from '../shared';
import OpenPredictionsSection from '../catalyst/open-predictions-section';
import HypothesesSection from '../catalyst/hypotheses-section';
import TrackRecordSection from '../catalyst/track-record-section';
import NewPredictionSection from '../catalyst/new-prediction-section';
import MethodologySection from '../catalyst/methodology-section';

const SECTIONS = [
  { key: 'predictions',  label: 'Open Predictions' },
  { key: 'hypotheses',   label: 'Hypotheses' },
  { key: 'track_record', label: 'Track Record' },
  { key: 'new',          label: 'New Prediction' },
  { key: 'methodology',  label: 'Methodology' },
];

export default function CatalystTab({ workstation, userId, supabase }) {
  const [section, setSection] = useState('hypotheses');

  if (!workstation) {
    return (
      <div className="ws-tab-content">
        <div className="ws-placeholder">
          <div className="ws-placeholder-eyebrow">Loading</div>
          <div className="ws-placeholder-title">Catalyst Research</div>
          <div className="ws-placeholder-body">Preparing Catalyst surface…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-tab-content">
      <SectionedTab
        eyebrow="Catalyst Research"
        title={sectionTitle(section)}
        subtitle={sectionSubtitle(section)}
        sections={SECTIONS}
        activeKey={section}
        onSelect={setSection}
      >
        {section === 'predictions'  && <OpenPredictionsSection userId={userId} />}
        {section === 'hypotheses'   && <HypothesesSection userId={userId} />}
        {section === 'track_record' && <TrackRecordSection userId={userId} />}
        {section === 'new'          && <NewPredictionSection userId={userId} workstation={workstation} />}
        {section === 'methodology'  && <MethodologySection userId={userId} />}
      </SectionedTab>
    </div>
  );
}

function sectionTitle(s) {
  if (s === 'predictions')  return 'Open Predictions';
  if (s === 'hypotheses')   return 'Hypothesis Library';
  if (s === 'track_record') return 'Track Record';
  if (s === 'new')          return 'Lock a New Prediction';
  if (s === 'methodology')  return 'Methodology';
  return 'Catalyst Research';
}

function sectionSubtitle(s) {
  if (s === 'predictions')  return 'Currently locked predictions with countdown to D+30 outcome.';
  if (s === 'hypotheses')   return 'H-001v2 + 6 children. The full hypothesis library — pre-registered, proposed, retired.';
  if (s === 'track_record') return 'Scored count vs excluded count. Sustain rate. Calibration over time.';
  if (s === 'new')          return 'Stimulus card lock — penny + catalyst sustain prediction.';
  if (s === 'methodology')  return 'Current version, change log, library, retired hypotheses.';
  return null;
}
